import type { ApiError, ErrorCode } from '@/types'

/**
 * The canonical user-facing sentence for every error code.
 *
 * The backend sends its own `message`; we prefer that. This table is the
 * fallback for client-side detection and for malformed/absent bodies, and it
 * is kept identical to the backend's copy on purpose.
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  NOT_YOUTUBE: 'Isso não parece um link do YouTube.',
  PLAYLIST_ONLY: 'Playlists ainda não são suportadas — cole o link de um vídeo.',
  IS_LIVE: 'Não dá para converter transmissões ao vivo.',
  UNAVAILABLE: 'Esse vídeo é privado ou não está disponível.',
  AGE_RESTRICTED: 'Esse vídeo tem restrição de idade.',
  GEO_BLOCKED: 'Esse vídeo não está disponível nesta região.',
  BOT_CHECK: 'O YouTube está limitando nossos acessos. Tente de novo em instantes.',
  DISK_FULL: 'O conversor está sem espaço agora. Tente mais tarde.',
  EXPIRED: 'Esse arquivo expirou. Converta de novo.',
  RATE_LIMITED: 'Você está indo rápido demais. Espere um minuto e tente de novo.',
  // The backend's cap is MAX_DURATION_SECONDS and it defaults to 1800, which
  // is the "30 minutes" here. Move one and move the other.
  TOO_LONG: 'Esse vídeo tem mais de 30 minutos. Tente um mais curto.',
  INTERNAL: 'Algo deu errado. Tente de novo.',
}

const ERROR_CODES = Object.keys(ERROR_MESSAGES) as ErrorCode[]

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as string[]).includes(value)
}

/** Build a fully-formed error from a known code. */
export function errorFromCode(code: ErrorCode, message?: string): ApiError {
  return { code, message: message ?? ERROR_MESSAGES[code] }
}

/**
 * Coerce an untrusted JSON body into an `ApiError`.
 *
 * Anything we cannot recognise degrades to `fallback` with our own copy, so a
 * proxy returning an HTML error page never leaks into the UI.
 */
export function toApiError(body: unknown, fallback: ErrorCode = 'INTERNAL'): ApiError {
  if (body && typeof body === 'object') {
    const candidate = body as { code?: unknown; message?: unknown }
    if (isErrorCode(candidate.code)) {
      const message =
        typeof candidate.message === 'string' && candidate.message.trim() !== ''
          ? candidate.message
          : ERROR_MESSAGES[candidate.code]
      return { code: candidate.code, message }
    }
  }
  return errorFromCode(fallback)
}
